<template>
  <!-- Admin-only: the /admin/sync-clickhouse/* endpoints require role ADMIN.
       Renders nothing for everyone else. -->
  <div v-if="isAdmin" class="d-inline-block">
    <!-- Tooltip on a span wrapper so the "sila tunggu" hint still shows while the
         button is disabled (a disabled v-btn swallows hover events itself). -->
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <span v-bind="attrs" v-on="on" class="d-inline-block">
          <!-- Badge dot + pulsing ring + bell icon ONLY while actionable, so an
               enabled button reads as "there is work waiting for you" rather than
               just "not disabled". All three switch off once it's selaras'd. -->
          <v-badge
            :value="needsAttention"
            color="orange accent-4"
            dot
            overlap
            bordered
          >
            <!-- `dark` ONLY while enabled. With `dark` always on, Vuetify renders the
                 disabled label as rgba(255,255,255,.3) over the light-grey disabled
                 background — i.e. white-on-white, so the button looked missing.
                 Bound this way it greys out normally: clearly inactive, still visible. -->
            <v-btn
              color="teal darken-2"
              class="text-none"
              :dark="!syncDisabled"
              :loading="syncStarting"
              :disabled="syncDisabled"
              :class="{ 'selaras-attention': needsAttention }"
              @click="openAndStart"
            >
              <v-icon left>{{ needsAttention ? 'mdi-bell-alert' : 'mdi-database-sync' }}</v-icon>
              Selaras Wajaran ke HID
            </v-btn>
          </v-badge>
        </span>
      </template>
      <span>{{ hintText }}</span>
    </v-tooltip>

    <v-dialog v-model="dialog" max-width="820" :persistent="streamOpen">
      <v-card>
        <v-card-title class="headline teal darken-2 white--text">
          Selaras Wajaran ke HID
          <v-spacer></v-spacer>
          <v-chip
            v-if="streamStatus"
            :color="streamStatusColor"
            small
            text-color="white"
            class="mr-2"
          >
            {{ streamStatus }}
          </v-chip>
          <v-btn icon dark :disabled="streamOpen" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="info" text dense class="mb-3">
            Selaras nilai wajaran bagi setiap pendapatan dan perbelanjaan HID untuk
            mengemaskini paparan modul Jadual Awalan, Pamer Data dan lain-lain.
            <div class="mt-1">
              Termasuk penjanaan semula data perbelanjaan — proses ini berat dan
              mengambil masa. Sila biarkan tetingkap ini terbuka sehingga selesai.
            </div>
          </v-alert>

          <v-alert v-if="runningBanner" type="warning" border="left" dense class="mb-3">
            {{ runningBanner }}
          </v-alert>

          <div v-if="totalTables > 0" class="mb-2">
            <div class="d-flex justify-space-between text-caption">
              <span>{{ doneTables }} / {{ totalTables }} bahagian</span>
              <span>{{ progressPct }}%</span>
            </div>
            <v-progress-linear
              :value="progressPct"
              :indeterminate="streamOpen && totalTables === 0"
              height="8"
              rounded
              color="teal"
            />
          </div>

          <v-card outlined>
            <div class="log-panel" ref="logPanel">
              <div
                v-for="(line, i) in logLines"
                :key="i"
                :class="['log-line', `log-${line.kind}`]"
              >
                <span class="log-ts">{{ line.ts }}</span>
                <span class="log-msg">{{ line.msg }}</span>
              </div>
              <div v-if="streamOpen" class="log-line log-info">
                <v-progress-circular indeterminate size="12" width="2" color="teal" />
                <span class="ml-2">Menyambung…</span>
              </div>
              <div v-if="logLines.length === 0 && !streamOpen" class="log-empty">
                Belum ada log.
              </div>
            </div>
          </v-card>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="streamOpen" @click="dialog = false">Tutup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * "Selaras Wajaran ke HID" button for the Wajaran page.
 *
 * Triggers GET /admin/sync-clickhouse/wajaran (SSE): the backend re-derives the
 * per-HID wajaran values from lampiran_wajaran, then refreshes the analytical
 * store that the Jadual Awalan / Pamer Data displays read from — under the same
 * job lock + history as the full sync. Mirrors the SSE handling in
 * views/db-status/ConnectionStatus.vue.
 *
 * Self-gated to ADMIN (the endpoint 403s otherwise). While any sync holds the
 * shared lock (this one, another admin's, the nightly cron, or a full sync) the
 * button is disabled with a "sila tunggu" hint; a 12s poll keeps that current.
 */
export default {
  name: 'WajaranSyncButton',
  data () {
    return {
      dialog: false,
      syncStarting: false,
      streamOpen: false,
      streamStatus: '',
      currentJobId: null,
      runningBanner: '',
      totalTables: 0,
      doneTables: 0,
      logLines: [],
      abortCtrl: null,

      // live lock status — disables the button while any sync holds the lock
      lockRunning: false,
      // false once the poll confirms lampiran_wajaran is unchanged since the last
      // selaras. Default true so the button is usable before the first probe.
      needSelaras: true,
      pollTimer: null,
    }
  },
  computed: {
    apiUrl () {
      return process.env.VUE_APP_API_URL
    },
    token () {
      return window.localStorage.getItem('access_token') || ''
    },
    isAdmin () {
      try {
        return ['ADMIN', 'SUPERADMIN'].includes((JSON.parse(window.localStorage.getItem('user')) || {}).role)
      } catch (e) {
        return false
      }
    },
    // Disabled while our own run is live/starting, while any other sync holds the
    // shared lock, right after a 409, or when there is nothing new to selaras.
    syncDisabled () {
      return this.streamOpen || this.syncStarting || this.lockRunning || !!this.runningBanner || !this.needSelaras
    },
    // Actionable right now: wajaran changed and nothing is blocking a run. Drives
    // the badge dot, the pulsing ring and the bell icon.
    needsAttention () {
      return !this.syncDisabled
    },
    // Hover text for every state — including the actionable one, which previously
    // had no tooltip at all.
    hintText () {
      if (this.streamOpen || this.syncStarting || this.lockRunning || this.runningBanner) {
        return 'Wujud Proses Selaras, Sila tunggu'
      }
      if (!this.needSelaras) {
        return 'Data wajaran sudah diselaras — tiada perubahan baharu.'
      }
      return 'Wajaran telah berubah — sila jalankan selaras untuk mengemaskini paparan.'
    },
    progressPct () {
      if (!this.totalTables) return 0
      return Math.min(100, Math.round((this.doneTables / this.totalTables) * 100))
    },
    streamStatusColor () {
      if (this.streamStatus === 'Selesai') return 'success'
      if (this.streamStatus === 'Gagal') return 'error'
      return 'primary'
    },
  },
  mounted () {
    // Component always mounts (root is v-if'd); only poll for admins who can
    // actually reach the endpoint.
    if (!this.isAdmin) return
    this.fetchRunningStatus()
    this.pollTimer = setInterval(this.fetchRunningStatus, 12000)
  },
  beforeDestroy () {
    if (this.abortCtrl) this.abortCtrl.abort()
    if (this.pollTimer) clearInterval(this.pollTimer)
  },
  methods: {
    async fetchRunningStatus () {
      if (!this.isAdmin) return
      // While our own stream is live we already know a sync is running.
      if (this.streamOpen) { this.lockRunning = true; return }
      try {
        const res = await fetch(`${this.apiUrl}/admin/sync-clickhouse/running`, {
          headers: { Authorization: `Bearer ${this.token}` },
        })
        if (!res.ok) return
        const data = await res.json()
        this.lockRunning = !!data.running
        // Undefined (older backend) → assume there IS something to selaras so the
        // button stays usable rather than mysteriously disabled.
        this.needSelaras = data.needSelaras !== false
      } catch (e) {
        // transient network error — keep last known state
      }
    },
    // Public: let the parent refresh state right after an edit/upload/generate.
    refresh () {
      this.fetchRunningStatus()
    },
    openAndStart () {
      this.dialog = true
      this.startSync()
    },
    resetSyncState () {
      this.logLines = []
      this.totalTables = 0
      this.doneTables = 0
      this.streamStatus = ''
      this.currentJobId = null
      this.runningBanner = ''
    },
    async startSync () {
      this.resetSyncState()
      this.syncStarting = true
      this.appendLog('info', 'Menghantar permintaan selaras…')

      this.abortCtrl = new AbortController()
      let response
      try {
        response = await fetch(`${this.apiUrl}/admin/sync-clickhouse/wajaran`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.token}`,
            Accept: 'text/event-stream',
          },
          signal: this.abortCtrl.signal,
        })
      } catch (err) {
        this.syncStarting = false
        this.appendLog('error', `Gagal menyambung: ${err.message}`)
        this.streamStatus = 'Gagal'
        return
      }

      if (response.status === 409) {
        let body = {}
        try { body = await response.json() } catch (e) { /* ignore */ }
        const job = body.job || {}
        const who = job.triggeredBy || 'pengguna lain'
        this.runningBanner = `Proses selaras sedang berjalan (dimulakan oleh ${who}). Sila tunggu sehingga ia selesai.`
        this.appendLog('warn', this.runningBanner)
        this.syncStarting = false
        this.fetchRunningStatus() // reflect the other run in the button state
        return
      }

      if (!response.ok) {
        this.syncStarting = false
        this.streamStatus = 'Gagal'
        const msg = response.status === 403
          ? 'Akses ditolak (perlu peranan ADMIN).'
          : `HTTP ${response.status}`
        this.appendLog('error', msg)
        return
      }

      this.syncStarting = false
      this.streamOpen = true
      this.streamStatus = 'Berjalan'

      try {
        await this.consumeSseStream(response)
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.appendLog('error', `Ralat: ${err.message}`)
          this.streamStatus = 'Gagal'
        }
      } finally {
        this.streamOpen = false
        this.abortCtrl = null
        // Only now is the whole job (sync + perbelanjaan rebuild) really done.
        if (this.streamStatus !== 'Gagal') this.streamStatus = 'Selesai'
        this.fetchRunningStatus() // refresh button state promptly after finishing
        this.$emit('synced')
      }
    },
    async consumeSseStream (response) {
      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buf = ''
      let streaming = true
      while (streaming) {
        const { done, value } = await reader.read()
        if (done) {
          streaming = false
          break
        }
        buf += decoder.decode(value, { stream: true })

        let sepIdx = buf.indexOf('\n\n')
        while (sepIdx !== -1) {
          const raw = buf.slice(0, sepIdx)
          buf = buf.slice(sepIdx + 2)
          this.handleSseBlock(raw)
          sepIdx = buf.indexOf('\n\n')
        }
      }
    },
    handleSseBlock (raw) {
      const lines = raw.split('\n')
      let event = 'message'
      let data = ''
      for (const line of lines) {
        if (!line || line.startsWith(':')) continue // heartbeat / comment
        if (line.startsWith('event:')) {
          event = line.slice(6).trim()
        } else if (line.startsWith('data:')) {
          data += (data ? '\n' : '') + line.slice(5).trim()
        }
      }
      if (!data && event === 'message') return

      let payload = null
      if (data) {
        try { payload = JSON.parse(data) } catch (e) { payload = { raw: data } }
      }
      this.handleSseEvent(event, payload || {})
    },
    handleSseEvent (event, d) {
      switch (event) {
        case 'job':
          this.currentJobId = d.jobId
          this.appendLog('info', `Proses #${d.jobId} dimulakan oleh ${d.triggeredBy || 'anda'}.`)
          break
        case 'enrich':
          // One friendly line for the whole phase — hide the raw per-column detail.
          if (this.streamStatus !== 'Menyelaras') {
            this.appendLog('info', 'Menyelaras nilai wajaran ke setiap HID…')
          }
          this.streamStatus = 'Menyelaras'
          break
        case 'start':
          this.streamStatus = 'Mengemaskini'
          this.totalTables = (d.tables || []).length
          this.doneTables = 0
          this.appendLog('info', `Mengemaskini paparan (${this.totalTables} bahagian)…`)
          break
        case 'table':
          this.doneTables += 1
          this.appendLog('ok', `[${d.table}] ${this.formatNumber(d.rows)} rekod dalam ${d.durationSec}s`)
          break
        case 'skip':
          this.doneTables += 1
          this.appendLog('warn', `[${d.table}] dilangkau — ${d.reason || 'tidak diketahui'}`)
          break
        case 'done':
          // NOT the end: the heavy lampiran_hid_perbelanjaan rebuild runs after
          // this. `finally` marks Selesai once the stream actually closes.
          this.appendLog('done', `Salinan selesai — ${d.tablesSynced} bahagian, ${this.formatNumber(d.totalRows)} rekod dalam ${d.durationSec}s.`)
          break
        case 'perbelanjaan':
          this.streamStatus = 'Menjana perbelanjaan'
          this.appendLog('info', d.message || 'Menjana data perbelanjaan…')
          break
        case 'perbelanjaan_error':
          // Non-fatal: the wajaran data is already synced, only the wide
          // expenditure table failed to rebuild.
          this.appendLog('warn', `Penjanaan perbelanjaan gagal: ${d.message || 'tidak diketahui'}`)
          break
        case 'error':
          this.streamStatus = 'Gagal'
          this.appendLog('error', `Ralat: ${d.message || 'tidak diketahui'}`)
          break
        default:
          // plan / retry / any future event — show its message if present.
          if (d && Object.keys(d).length) {
            this.appendLog('info', `[${event}] ${d.message || JSON.stringify(d)}`)
          }
      }
    },
    appendLog (kind, msg) {
      this.logLines.push({ kind, msg, ts: this.nowHms() })
      this.$nextTick(() => {
        const el = this.$refs.logPanel
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    nowHms () {
      const d = new Date()
      return [d.getHours(), d.getMinutes(), d.getSeconds()]
        .map(n => String(n).padStart(2, '0'))
        .join(':')
    },
    formatNumber (n) {
      if (n == null) return '-'
      return Number(n).toLocaleString('en-US')
    },
  },
}
</script>

<style scoped>
/* Pulsing ring while a selaras is pending. Drawn on an ::after overlay rather
   than the button's own box-shadow, so Vuetify's elevation isn't clobbered
   (animating box-shadow directly makes the button flicker flat). The class sits
   on the v-btn root, which scoped CSS can target without ::v-deep. */
.selaras-attention {
  position: relative;
}
.selaras-attention::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: inherit;
  pointer-events: none;
  animation: selaras-pulse 1.8s ease-out infinite;
}
@keyframes selaras-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(255, 145, 0, 0.55); }
  70%  { box-shadow: 0 0 0 12px rgba(255, 145, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 145, 0, 0); }
}
/* Respect users who ask the OS to reduce motion — badge + icon still convey it. */
@media (prefers-reduced-motion: reduce) {
  .selaras-attention::after { animation: none; }
}

.log-panel {
  background: #0e1117;
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 12px;
  line-height: 1.5;
  padding: 10px 12px;
  height: 300px;
  overflow-y: auto;
  border-radius: 4px;
}
.log-line {
  white-space: pre-wrap;
  word-break: break-word;
}
.log-ts {
  color: #6b7280;
  margin-right: 8px;
}
.log-info .log-msg { color: #d4d4d4; }
.log-ok .log-msg { color: #22c55e; }
.log-warn .log-msg { color: #f59e0b; }
.log-error .log-msg { color: #ef4444; }
.log-done .log-msg { color: #38bdf8; font-weight: 600; }
.log-empty {
  color: #6b7280;
  text-align: center;
  padding: 24px 0;
}
</style>
